import React, { useCallback, useEffect, useMemo, useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Popover from '@mui/material/Popover'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import { MapPinTypeVariant } from '../MapPin/types'
import { KeyInformationMapPin } from './KeyInformationMapPin'
import { useAppDispatch, useAppSelector } from '@user/store/hooks'
import { selectAllKnockStatuses } from '@user/store/knockStatus/slice'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import { usePestPacPinsEnabled } from '@user/store/tenantFeatures/hooks/usePestPacPinsEnabled'
import { KnockStatusIcon } from '@user/components/KnockStatus'
import { getAllKnockStatuses } from '@user/store/knockStatus/api'

export function KeyInformation() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const dispatch = useAppDispatch()
  const d2dKnockEnabled = useD2DKnockEnabled()
  const pestPacPinsEnabled = usePestPacPinsEnabled()

  const records = useMemo(
    () => [
      {
        title: 'Pending Lead',
        icon: <KeyInformationMapPin variant={MapPinTypeVariant.PendingLead} />,
      },
      {
        title: 'Lead',
        icon: <KeyInformationMapPin variant={MapPinTypeVariant.Lead} />,
      },
      {
        title: 'Opportunity',
        icon: <KeyInformationMapPin variant={MapPinTypeVariant.Opportunity} />,
      },
      ...(pestPacPinsEnabled
        ? [
            {
              title: 'PestPac Account',
              icon: (
                <KeyInformationMapPin variant={MapPinTypeVariant.PestPac} />
              ),
            },
          ]
        : []),
      {
        title: 'Multiple records',
        icon: <KeyInformationMapPin variant={MapPinTypeVariant.Mixed} />,
      },
    ],
    [pestPacPinsEnabled],
  )
  const knockStatuses = useAppSelector(selectAllKnockStatuses)
  // Load knock statuses when D2D is enabled
  useEffect(() => {
    if (d2dKnockEnabled) {
      dispatch(getAllKnockStatuses())
    }
  }, [d2dKnockEnabled, dispatch])

  const open = Boolean(anchorEl)

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget)
    },
    [],
  )

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <Box>
      <Paper elevation={4}>
        <IconButton sx={{ p: 1 }} onClick={handleClick}>
          <VpnKeyIcon fontSize="small" />
        </IconButton>
      </Paper>
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              transform: 'translateX(-16px) translateY(-12px) !important',
              borderRadius: '8px',
              minWidth: '360px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              backgroundColor: '#F9FAFB',
              pt: 1.6,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            px: 2.4,
          }}
        >
          <Typography variant="h6">Key</Typography>
          <IconButton onClick={handleClose} data-test-id="mapKeyCloseBtn">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          data-test-id="mapKeyScrollBody"
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            px: 2.4,
            pb: 0.8,
          }}
        >
          <Box
            sx={{
              mt: 1.2,
              mb: 1.6,
              backgroundColor: 'white',
              pt: 1.8,
              pb: 1.6,
              px: 1.6,
              borderRadius: 0.8,
            }}
          >
            <Typography variant="subtitle2">Records</Typography>
            <Box
              mt={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.6,
              }}
            >
              {records.map((record) => (
                <Box
                  key={record.title}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.6,
                  }}
                >
                  {record.icon}
                  <Typography variant="body1">{record.title}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          {d2dKnockEnabled && knockStatuses.length > 0 && (
            <Box
              sx={{
                mb: 1.6,
                backgroundColor: 'white',
                pt: 1.8,
                pb: 1.6,
                px: 1.6,
                borderRadius: 0.8,
              }}
            >
              <Typography variant="subtitle2">Knock Statuses</Typography>
              <Box
                mt={1}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {knockStatuses.map((status) => (
                  <Box
                    key={status.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      ml: '-14px',
                    }}
                  >
                    <KnockStatusIcon
                      color={status.color}
                      icon={status.icon}
                      width={55}
                      height={55}
                      baseIconSize={12}
                      viewBox="0 0 51 56"
                    />
                    <Typography variant="body1" mb={'4px'}>
                      {status.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  )
}
